// src/ProjectPage.tsx
// One project: its name, book and status; the draft preview (src/preview/)
// beside the visual editor (src/editor/), with the make_video spec as raw
// JSON on a second tab as the escape hatch; and the Render button with this
// project's recent renders. The JSON text is the one source of truth: the
// visual editor edits the parsed spec and writes it back as text, so the
// two tabs always agree.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { isActiveMember } from '@ring/shared/author-trust';
import type { AssetKind } from '@ring/shared/assets';
import { BookSelect } from './BookSelect';
import {
  ASSET_REF_PREFIX, MAX_PROJECT_NAME, parseAssetRef, specFileSlots, type ProjectStatus,
} from '@ring/shared/projects';
import type { SnippetKind } from '@ring/shared/snippets';
import { getProject, updateProject, deleteProject, queueRender, type Project } from './projectsApi';
import { listAssets, type AssetSummary } from './assetsApi';
import { listSnippets, createSnippet, deleteSnippet, type Snippet } from './snippetsApi';
import { useJobs, JobsTable, WorkerBanner, isActiveJob } from './Jobs';
import { Player } from './preview/Player';
import type { AssetLookup } from './preview/stage';
import type { Json } from './preview/spec';
import { SpecEditor } from './editor/SpecEditor';
import './Projects.css';
import './editor/Editor.css';

type Parsed = { ok: true; spec: Record<string, unknown> } | { ok: false; error: string };

function parseSpecText(text: string): Parsed {
  try {
    const spec = JSON.parse(text);
    if (!spec || typeof spec !== 'object' || Array.isArray(spec)) return { ok: false, error: 'The spec must be a JSON object ({ ... }).' };
    return { ok: true, spec };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Invalid JSON.' };
  }
}

const formatSpec = (spec: unknown) => JSON.stringify(spec, null, 2);

/** One-click placements from the JSON tab's library panel: [label, top-level object, field]. */
const PLACEMENTS: Record<AssetKind, [string, string, string][]> = {
  image: [['Background', 'background', 'file'], ['End card', 'endCard', 'image']],
  video: [['Background', 'background', 'file']],
  music: [['Music', 'audio', 'file']],
};

type Tab = 'editor' | 'json';

export function ProjectPage() {
  const { projectId = '' } = useParams();
  const { author, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const active = author ? isActiveMember(author.authorStatus) : false;

  const [project, setProject]   = useState<Project | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName]         = useState('');
  const [bookCode, setBookCode] = useState('');
  const [status, setStatus]     = useState<ProjectStatus>('draft');
  const [specText, setSpecText] = useState('');
  const [dirty, setDirty]       = useState(false);
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [notice, setNotice]     = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [assets, setAssets]     = useState<AssetSummary[]>([]);
  const [assetsState, setAssetsState] = useState<'loading' | 'loaded' | 'failed'>('loading');
  const [assetQuery, setAssetQuery] = useState('');
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [tab, setTab]           = useState<Tab>('editor');
  const [currentBeat, setCurrentBeat] = useState(0);
  const [seek, setSeek]         = useState<{ t: number; nonce: number; play: boolean; ifPaused?: boolean } | undefined>();

  const { jobs, worker, error: jobsError, refresh: refreshJobs } = useJobs({ projectId, limit: 10, enabled: active && project !== null });

  useEffect(() => {
    if (!authLoading && (!author || !active)) navigate('/dashboard');
  }, [authLoading, author, active, navigate]);

  const seed = useCallback((p: Project) => {
    setProject(p);
    setName(p.name);
    setBookCode(p.bookCode ?? '');
    setStatus(p.status);
    setSpecText(formatSpec(p.spec));
    setDirty(false);
  }, []);

  useEffect(() => {
    if (!active) return;
    getProject(projectId)
      .then(seed)
      .catch(err => setLoadError(err instanceof Error ? err.message : 'Failed to load the project.'));
    setAssetsState('loading');
    listAssets()
      .then(list => { setAssets(list.assets); setAssetsState('loaded'); })
      .catch(() => setAssetsState('failed'));
    listSnippets().then(setSnippets).catch(() => { /* the snippet menu just stays empty */ });
  }, [active, projectId, seed]);

  // Leaving with unsaved edits asks first.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const parsed = useMemo(() => parseSpecText(specText), [specText]);
  const previewSpec = parsed.ok ? parsed.spec as Json : null;
  const assetsById = useMemo(() => new Map(assets.map(a => [a.assetId, a])), [assets]);
  /** Asset ids the spec currently uses, for the "in use" marks in the JSON tab's library panel. */
  const usedIds = useMemo(() => {
    if (!parsed.ok) return new Set<string>();
    return new Set(specFileSlots(parsed.spec).map(s => parseAssetRef(s.value)).filter((id): id is string => id !== null));
  }, [parsed]);

  /** The preview reads library files from the same same-origin URLs the Library page uses. */
  const lookup = useCallback<AssetLookup>(value => {
    const id = parseAssetRef(value);
    if (!id) return { missing: 'only files from your library can be used.' };
    const a = assetsById.get(id);
    if (!a) {
      return {
        missing: assetsState === 'loading' ? 'loading your library…'
          : assetsState === 'failed' ? 'your library couldn\'t be loaded. Reload the page to try again.'
          : 'that file isn\'t in your library (was it deleted?).',
      };
    }
    return { url: a.fileUrl, kind: a.kind, filename: a.filename, contentType: a.contentType };
  }, [assetsById, assetsState]);

  /** Counts edits, so a save can tell whether anything changed while it was on its way. */
  const editCount = useRef(0);

  function edit<T>(setter: (v: T) => void) {
    return (v: T) => { setter(v); setDirty(true); setNotice(null); editCount.current++; };
  }

  const editSpec = useCallback((spec: Json) => {
    setSpecText(formatSpec(spec));
    setDirty(true);
    setNotice(null);
    editCount.current++;
  }, []);

  async function save(): Promise<boolean> {
    if (!project) return false;
    if (!parsed.ok) { setError(`Fix the JSON first: ${parsed.error}`); return false; }
    setBusy(true);
    setError(null);
    const editsAtSave = editCount.current;
    try {
      const saved = await updateProject(project.projectId, { name, bookCode: bookCode || null, status, spec: parsed.spec });
      // Edits made while saving stay on screen, still unsaved, rather than being replaced by what was sent
      if (editCount.current === editsAtSave) seed(saved);
      else setProject(saved);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function render() {
    if (!project || dirty) return; // the button is disabled until changes are saved
    setBusy(true);
    setError(null);
    try {
      await queueRender(project.projectId);
      setNotice('Render queued.');
      await refreshJobs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Couldn\'t queue the render.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!project) return;
    setBusy(true);
    try {
      await deleteProject(project.projectId);
      setDirty(false);
      navigate('/dashboard/projects');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.');
      setBusy(false);
    }
  }

  function place(asset: AssetSummary, objectKey: string, field: string) {
    if (!parsed.ok) return;
    const spec = structuredClone(parsed.spec);
    const holder = spec[objectKey] && typeof spec[objectKey] === 'object' && !Array.isArray(spec[objectKey])
      ? spec[objectKey] as Record<string, unknown> : {};
    spec[objectKey] = { ...holder, [field]: ASSET_REF_PREFIX + asset.assetId };
    edit(setSpecText)(formatSpec(spec));
  }

  async function saveSnippet(kind: SnippetKind, snippetName: string, fragment: Json) {
    const s = await createSnippet({ name: snippetName, kind, fragment });
    setSnippets(prev => [...prev, s].sort((a, b) => a.kind.localeCompare(b.kind) || a.name.localeCompare(b.name)));
  }

  async function removeSnippet(snippetId: string) {
    await deleteSnippet(snippetId);
    setSnippets(prev => prev.filter(s => s.snippetId !== snippetId));
  }

  const playFrom = useCallback((t: number) => setSeek(prev => ({ t, nonce: (prev?.nonce ?? 0) + 1, play: true })), []);
  /** Moving into a beat or the end card in the editor shows it in the preview, if the preview is paused. */
  const showAt = useCallback((t: number) => setSeek(prev => ({ t, nonce: (prev?.nonce ?? 0) + 1, play: false, ifPaused: true })), []);

  if (authLoading || !author || !active) return null;
  if (loadError) {
    return (
      <div className="projects-page">
        <Link className="page-back-link" to="/dashboard/projects">← Projects</Link>
        <p className="page-error-text">{loadError}</p>
      </div>
    );
  }
  if (!project) return <div className="projects-page"><p className="projects-sub">Loading…</p></div>;

  // One render per project at a time (the API refuses a second while one is waiting or running)
  const rendering = jobs?.some(isActiveJob) ?? false;

  const q = assetQuery.trim().toLowerCase();
  const shownAssets = assets.filter(a => !q || [a.filename, a.descShort, a.bookCode, ...a.tags, ...a.roles]
    .filter(Boolean).join(' ').toLowerCase().includes(q));

  return (
    <div
      className="projects-page projects-page-wide"
      onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); if (dirty && !busy) void save(); } }}
    >
      <Link className="page-back-link" to="/dashboard/projects">← Projects</Link>

      {/* Book \ name, like the folder the render is saved in. A name is unique within its book. */}
      <div className="projects-meta">
        <label className="projects-field">
          Book
          <BookSelect value={bookCode} onChange={edit(setBookCode)} />
        </label>
        <span className="book-path-sep" aria-hidden="true">\</span>
        <label className="projects-field projects-field-grow">
          Name
          <input type="text" maxLength={MAX_PROJECT_NAME} value={name} onChange={e => edit(setName)(e.target.value)} />
        </label>
        <label className="projects-field">
          Status
          <select value={status} onChange={e => edit(setStatus)(e.target.value as ProjectStatus)}>
            <option value="draft">Draft</option>
            <option value="ready">Ready</option>
          </select>
        </label>
      </div>

      <div className="projects-actions">
        {/* Saving is the everyday action; rendering is the deliberate final step, of what's been saved */}
        <button className="projects-primary" disabled={busy || !dirty || !parsed.ok} onClick={() => void save()}>Save</button>
        <button className="projects-secondary" disabled={busy || dirty || !parsed.ok || rendering} onClick={render}
          title={rendering ? 'A render of this project is already waiting or in progress'
            : dirty ? 'Save your changes first: the render uses the saved version' : 'Make the final video from the saved version'}>
          Render final video
        </button>
        {confirmDelete ? (
          <span className="projects-delete-confirm">
            <button className="projects-danger" disabled={busy} onClick={remove}
              title="Its renders are deleted too, from the Renders page as well. This can't be undone.">Delete project and its renders</button>
            <button className="projects-secondary" disabled={busy} onClick={() => setConfirmDelete(false)}>Cancel</button>
          </span>
        ) : (
          <button className="projects-link-btn" disabled={busy} onClick={() => setConfirmDelete(true)}>Delete…</button>
        )}
      </div>
      {error && <pre className="projects-error">{error}</pre>}
      {notice && <p className="projects-notice">{notice}</p>}

      <div className="project-workspace">
        <section className="project-preview" aria-label="Preview">
          <Player spec={previewSpec} lookup={lookup} name={name} seek={seek} onBeat={setCurrentBeat} />
        </section>

        <section>
          <div className="project-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'editor'} className={`project-tab${tab === 'editor' ? ' project-tab-on' : ''}`}
              onClick={() => setTab('editor')}>Editor</button>
            <button type="button" role="tab" aria-selected={tab === 'json'} className={`project-tab${tab === 'json' ? ' project-tab-on' : ''}`}
              onClick={() => setTab('json')}>JSON</button>
          </div>

          {assetsState === 'failed' && (
            <p className="page-error-text">Your library couldn't be loaded, so files can't be chosen or previewed. Reload the page to try again.</p>
          )}
          {tab === 'editor' ? (
            parsed.ok ? (
              <SpecEditor spec={parsed.spec as Json} onChange={editSpec} assets={assets} assetsById={assetsById}
                snippets={snippets} onSaveSnippet={saveSnippet} onDeleteSnippet={removeSnippet}
                currentBeat={currentBeat} onPlayFrom={playFrom} onShow={showAt} />
            ) : (
              <p className="page-error-text">
                The JSON has an error, so the editor can't show it: {parsed.error}{' '}
                <button type="button" className="editor-text-btn" onClick={() => setTab('json')}>Fix it in the JSON tab</button>
              </p>
            )
          ) : (
            <>
              <div className="projects-spec-head">
                <span className="projects-sub">
                  make_video JSON. Files are <code>asset:&lt;id&gt;</code> from your library; <code>""</code> means none.
                </span>
              </div>
              <textarea
                className={`projects-spec-text${parsed.ok ? '' : ' projects-spec-invalid'}`}
                value={specText}
                onChange={e => edit(setSpecText)(e.target.value)}
                spellCheck={false}
                rows={28}
                aria-label="Spec JSON"
              />
              {!parsed.ok && <p className="page-error-text">JSON: {parsed.error}</p>}

              <aside className="projects-assets">
                <h2>Library</h2>
                <input type="search" placeholder="Search" value={assetQuery} onChange={e => setAssetQuery(e.target.value)} />
                {assets.length === 0 ? (
                  <p className="projects-sub">Nothing in your <Link to="/dashboard/library">library</Link> yet.</p>
                ) : (
                  <ul className="projects-asset-list">
                    {shownAssets.map(a => (
                      <li key={a.assetId} className={`projects-asset${usedIds.has(a.assetId) ? ' projects-asset-used' : ''}`}>
                        <span className="projects-asset-thumb">
                          {a.thumbUrl
                            ? <img src={a.thumbUrl} alt="" loading="lazy" />
                            : <span aria-hidden="true">{a.kind === 'music' ? '♪' : a.kind === 'video' ? '▶' : '▢'}</span>}
                        </span>
                        <span className="projects-asset-body">
                          <span className="projects-asset-name" title={a.filename}>{a.filename}</span>
                          <span className="projects-asset-buttons">
                            {PLACEMENTS[a.kind].map(([label, obj, field]) => (
                              <button key={label} type="button" disabled={!parsed.ok} onClick={() => place(a, obj, field)}>{label}</button>
                            ))}
                            <button type="button" title="Copy asset:<id> to paste anywhere in the spec"
                              onClick={() => void navigator.clipboard.writeText(ASSET_REF_PREFIX + a.assetId).catch(() => {})}>
                              Copy ref
                            </button>
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {usedIds.size > 0 && [...usedIds].some(id => !assetsById.has(id)) && (
                  <p className="page-error-text">The spec uses an asset that isn't in your library.</p>
                )}
              </aside>
            </>
          )}
        </section>
      </div>

      <section className="projects-renders">
        <h2>Renders</h2>
        <WorkerBanner worker={worker} jobs={jobs} />
        {jobsError && <p className="page-error-text">{jobsError}</p>}
        {jobs && jobs.length > 0
          ? <JobsTable jobs={jobs} showProject={false} onChanged={() => void refreshJobs()} />
          : jobs && <p className="projects-sub">Not rendered yet. Press Render final video when it's ready.</p>}
      </section>
    </div>
  );
}
